import { rgba } from 'polished';
import { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useLanguage } from '@treams/treams-hooks';
import { CircleUserIcon } from '@treams/treams-icons';
import { tokens } from '@treams/treams-ui';

declare global {
	interface Window {
		hbspt?: {
			forms: {
				create: (opts: {
					portalId: string;
					formId: string;
					region?: string;
					target?: string;
				}) => void;
			};
		};
	}
}

const HUBSPOT_SCRIPT_SRC = 'https://js-eu1.hsforms.net/forms/embed/v2.js';
const HUBSPOT_SCRIPT_ATTR = 'data-treams-hubspot';
const HUBSPOT_PORTAL_ID = '6955348';
const HUBSPOT_REGION = 'eu1';
const FORM_ID_DUTCH = '22e77e24-e5eb-4c61-91a8-37564df37316';
const FORM_ID_DEFAULT = 'c3fb5a87-1ae9-4665-99ec-5b60c8518775';
const FORM_CONTAINER_ID = 'treams-hubspot-form';

function loadHubspot(): Promise<void> {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('window is not available'));
	}

	if (window.hbspt) {
		return Promise.resolve();
	}

	const existing = document.querySelector<HTMLScriptElement>(`script[${HUBSPOT_SCRIPT_ATTR}]`);
	if (existing) {
		return new Promise((resolve, reject) => {
			existing.addEventListener('load', () => resolve(), { once: true });
			existing.addEventListener(
				'error',
				() => reject(new Error('Failed to load HubSpot embed script')),
				{ once: true },
			);
		});
	}

	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = HUBSPOT_SCRIPT_SRC;
		script.async = true;
		script.defer = true;
		script.setAttribute(HUBSPOT_SCRIPT_ATTR, '');
		script.addEventListener('load', () => resolve(), { once: true });
		script.addEventListener(
			'error',
			() => reject(new Error('Failed to load HubSpot embed script')),
			{ once: true },
		);
		document.head.appendChild(script);
	});
}

const Page = styled.div`
	padding: 36px;
`;

const Split = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 36px;
	align-items: flex-start;
`;

const Block = styled.div`
	flex: 1 1 360px;
	min-width: 0;
`;

const Visual = styled.div`
	background: ${(props) => rgba(props.theme.colors.primaryVibrant, 0.06)};
	border-radius: 16px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const RewardsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
`;

const RewardCard = styled.div`
	flex: 1 1 160px;
	min-width: 0;
	background: white;
	border-radius: 12px;
	padding: 16px 20px;
`;

const Eyebrow = styled.div`
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
	color: ${(props) => props.theme.colors.primary};
	margin-bottom: 6px;
`;

const RewardAmount = styled.div`
	font-size: 28px;
	font-weight: 700;
	color: ${tokens.text.primary};
	line-height: 1.2;
`;

const RewardCaption = styled.div`
	margin-top: 4px;
	font-size: 14px;
	color: ${tokens.text.secondary};
`;

const Card = styled.div`
	background: white;
	border-radius: 12px;
	padding: 16px 20px;
`;

const CardTitle = styled.div`
	font-size: 18px;
	font-weight: 700;
	color: ${tokens.text.primary};
	margin-bottom: 12px;
`;

const StepRow = styled.div`
	display: flex;
	gap: 12px;
	align-items: flex-start;
	padding: 8px 0;
`;

const StepNumber = styled.div`
	flex: 0 0 auto;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: ${(props) => rgba(props.theme.colors.primary, 0.12)};
	color: ${(props) => props.theme.colors.primary};
	font-size: 13px;
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const StepText = styled.div`
	font-size: 14px;
	color: ${tokens.text.primary};
	line-height: 1.5;
`;

const TestimonialCard = styled.div`
	background: ${(props) => rgba(props.theme.colors.primaryVibrant, 0.1)};
	border-radius: 12px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const Quote = styled.div`
	font-size: 14px;
	font-style: italic;
	color: ${tokens.text.primary};
	line-height: 1.5;
`;

const AuthorRow = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
`;

const AuthorAvatar = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: ${(props) => rgba(props.theme.colors.primary, 0.18)};
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${(props) => props.theme.colors.primary};

	svg {
		width: 20px;
		height: 20px;
	}
`;

const AuthorName = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: ${tokens.text.primary};
`;

const AuthorMeta = styled.div`
	font-size: 13px;
	color: ${tokens.text.secondary};
`;

const Heading = styled.h1`
	font-size: 24px;
	font-weight: 700;
	color: ${tokens.text.primary};
	margin: 0 0 4px;
`;

const Subtitle = styled.p`
	font-size: 14px;
	color: ${tokens.text.secondary};
	max-width: 420px;
	line-height: 1.5;
`;

function RecommendDutch({ children }: { children: ReactNode }) {
	return (
		<Page>
			<Heading>Jouw netwerk verdient ook Treams</Heading>
			<Split>
				<Block>
					<Subtitle>
						Jij weet wat het doet als een organisatie écht investeert in haar mensen. Ken jij iemand
						in HR die daar ook mee bezig is en Treams zou kunnen gebruiken? Breng ons dan met elkaar
						in contact.
					</Subtitle>
					{children}
				</Block>
				<Block>
					<Visual>
						<RewardsRow>
							<RewardCard>
								<Eyebrow>Jij ontvangt</Eyebrow>
								<RewardAmount>€ 200</RewardAmount>
								<RewardCaption>cadeaubon naar keuze</RewardCaption>
							</RewardCard>
							<RewardCard>
								<Eyebrow>Jouw aanbeveling ontvangt</Eyebrow>
								<RewardAmount>1 gratis workshop</RewardAmount>
								<RewardCaption>naar keuze</RewardCaption>
							</RewardCard>
						</RewardsRow>

						<Card>
							<CardTitle>Wat kun je verwachten?</CardTitle>
							<StepRow>
								<StepNumber>1</StepNumber>
								<StepText>Wij nemen contact op met de persoon die jij aanbeveelt.</StepText>
							</StepRow>
							<StepRow>
								<StepNumber>2</StepNumber>
								<StepText>Samen kijken we of Treams past bij hun situatie.</StepText>
							</StepRow>
							<StepRow>
								<StepNumber>3</StepNumber>
								<StepText>Als er een goede match is, plannen we een vervolg in.</StepText>
							</StepRow>
							<StepRow>
								<StepNumber>4</StepNumber>
								<StepText>
									Worden zij klant? Dan bedanken we jou met <strong>een cadeaubon</strong> en maakt
									jouw aanbeveling een vliegende start met een <strong>gratis workshop</strong>.
								</StepText>
							</StepRow>
						</Card>

						<TestimonialCard>
							<Quote>
								&ldquo;Wat me overtuigde was dat mijn collega precies hetzelfde probleem had gehad.
								Als zij er blij van werden, wist ik dat het bij ons ook zou werken.&rdquo;
							</Quote>
							<AuthorRow>
								<AuthorAvatar>
									<CircleUserIcon />
								</AuthorAvatar>
								<div>
									<AuthorName>HR Directeur</AuthorName>
									<AuthorMeta>Advocatenkantoor · 320 medewerkers</AuthorMeta>
								</div>
							</AuthorRow>
						</TestimonialCard>
					</Visual>
				</Block>
			</Split>
		</Page>
	);
}

function RecommendEnglish({ children }: { children: ReactNode }) {
	return (
		<Page>
			<Heading>Your network deserves Treams too</Heading>
			<Split>
				<Block>
					<Subtitle>
						You know the difference it makes when an organisation truly invests in its people. Do
						you know someone in HR who shares that mindset and could use Treams? Introduce us.
					</Subtitle>
					{children}
				</Block>
				<Block>
					<Visual>
						<RewardsRow>
							<RewardCard>
								<Eyebrow>You receive</Eyebrow>
								<RewardAmount>€ 200</RewardAmount>
								<RewardCaption>gift card of your choice</RewardCaption>
							</RewardCard>
							<RewardCard>
								<Eyebrow>Your referral receives</Eyebrow>
								<RewardAmount>1 free workshop</RewardAmount>
								<RewardCaption>of their choice</RewardCaption>
							</RewardCard>
						</RewardsRow>

						<Card>
							<CardTitle>What can you expect?</CardTitle>
							<StepRow>
								<StepNumber>1</StepNumber>
								<StepText>We&apos;ll reach out to the person you recommend.</StepText>
							</StepRow>
							<StepRow>
								<StepNumber>2</StepNumber>
								<StepText>Together we&apos;ll see if Treams fits their situation.</StepText>
							</StepRow>
							<StepRow>
								<StepNumber>3</StepNumber>
								<StepText>If it&apos;s a good match, we&apos;ll schedule a follow-up.</StepText>
							</StepRow>
							<StepRow>
								<StepNumber>4</StepNumber>
								<StepText>
									Do they become a customer? We&apos;ll thank you with <strong>a gift card</strong>{' '}
									and your referral gets a flying start with a <strong>free workshop</strong>.
								</StepText>
							</StepRow>
						</Card>

						<TestimonialCard>
							<Quote>
								&ldquo;What convinced me was that a colleague had had exactly the same problem. If
								it worked for them, I knew it would work for us too.&rdquo;
							</Quote>
							<AuthorRow>
								<AuthorAvatar>
									<CircleUserIcon />
								</AuthorAvatar>
								<div>
									<AuthorName>HR Director</AuthorName>
									<AuthorMeta>Law firm · 320 employees</AuthorMeta>
								</div>
							</AuthorRow>
						</TestimonialCard>
					</Visual>
				</Block>
			</Split>
		</Page>
	);
}

export default function Recommend() {
	const { id: locale, loading: localeLoading } = useLanguage();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const isDutch = locale === 'nl_NL';

	useEffect(() => {
		if (localeLoading) return;

		let cancelled = false;

		const formId = isDutch ? FORM_ID_DUTCH : FORM_ID_DEFAULT;
		const container = containerRef.current;

		loadHubspot()
			.then(() => {
				if (cancelled || !window.hbspt) return;

				window.hbspt.forms.create({
					portalId: HUBSPOT_PORTAL_ID,
					formId,
					region: HUBSPOT_REGION,
					target: `#${FORM_CONTAINER_ID}`,
				});
			})
			.catch(() => {});

		return () => {
			cancelled = true;
			if (container) {
				container.innerHTML = '';
			}
		};
	}, [isDutch, localeLoading]);

	const formSlot = <div ref={containerRef} id={FORM_CONTAINER_ID} />;

	if (!isDutch) {
		return <RecommendEnglish>{formSlot}</RecommendEnglish>;
	}

	return <RecommendDutch>{formSlot}</RecommendDutch>;
}
